import { buildSrhtExamOverallReportContent } from './srhtExamPdfOverallLayout.js';
import { buildSrhtFinalEvaluation } from './srhtExamFinalEvaluation.js';

const PRACTICAL_STATION_LABELS = {
  fitness_coordination: 'Prüfbogen 1 Fitness und Koordination',
  aktivrettung: 'Prüfbogen 2 Aktivrettung',
  einzelueberpruefung: 'Prüfbogen 5 Einzelüberprüfung',
};

export const SRHT_EXAM_PDF_OPTIONS = [
  {
    value: 'exam-report-all',
    label: 'Gesamtbericht Prüfung',
    requiresParticipantSelection: false,
    includesAssessment: false,
    includesCourse: false,
    includesExam: true,
  },
  {
    value: 'exam-report-selection',
    label: 'Sammelbericht Teilnehmer',
    requiresParticipantSelection: true,
    includesAssessment: false,
    includesCourse: false,
    includesExam: true,
  },
  {
    value: 'exam-report-single',
    label: 'Einzelbericht Teilnehmer',
    requiresParticipantSelection: true,
    includesAssessment: false,
    includesCourse: false,
    includesExam: true,
  },
  {
    value: 'full-chain-all',
    label: 'Gesamtbericht Eignungsverfahren + Lehrgang + Prüfung',
    requiresParticipantSelection: false,
    includesAssessment: true,
    includesCourse: true,
    includesExam: true,
  },
  {
    value: 'full-chain-selection',
    label: 'Sammelbericht Teilnehmer Eignungsverfahren + Lehrgang + Prüfung',
    requiresParticipantSelection: true,
    includesAssessment: true,
    includesCourse: true,
    includesExam: true,
  },
  {
    value: 'full-chain-single',
    label: 'Einzelbericht Teilnehmer Eignungsverfahren + Lehrgang + Prüfung',
    requiresParticipantSelection: true,
    includesAssessment: true,
    includesCourse: true,
    includesExam: true,
  },
];

function formatDate(value) {
  const normalized = String(value || '').trim();
  if (!normalized) {
    return '-';
  }

  const parsed = new Date(normalized);
  if (Number.isNaN(parsed.getTime())) {
    return normalized;
  }

  return parsed.toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric' });
}

function formatDateTime(value) {
  const normalized = String(value || '').trim();
  if (!normalized) {
    return '-';
  }

  const parsed = new Date(normalized);
  if (Number.isNaN(parsed.getTime())) {
    return normalized;
  }

  return parsed.toLocaleString('de-DE', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

function formatNumber(value, digits = 2) {
  const numeric = Number(value);
  if (!Number.isFinite(numeric)) {
    return '-';
  }

  return Number(numeric.toFixed(digits)).toLocaleString('de-DE');
}

function formatQualifiedLabel(value) {
  if (value === true) {
    return 'Bestanden';
  }
  if (value === false) {
    return 'Nicht bestanden';
  }
  return 'Offen';
}

function formatExamStatusLabel(value) {
  if (value === 'closed') {
    return 'Abgeschlossen';
  }
  if (value === 'active') {
    return 'Aktiv';
  }
  if (value === 'draft') {
    return 'Entwurf';
  }
  return String(value || '-');
}

function formatPracticalStatusLabel(value) {
  if (value === 'finalized' || value === 'final') {
    return 'Finalisiert';
  }
  if (value === 'saved') {
    return 'Gespeichert';
  }
  if (value === 'not_started') {
    return 'Nicht begonnen';
  }
  return value ? String(value) : 'Entwurf';
}

function formatCandidateRoleVariant(value) {
  return String(value || '').trim() === 'bewerber_srht_extern'
    ? 'Externer Bewerber'
    : 'Bewerber';
}

function sanitizeFilenamePart(value) {
  return String(value || '')
    .normalize('NFKD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^\x20-\x7E]/g, '')
    .replace(/[\\/:*?"<>|]/g, '')
    .trim();
}

function buildKeyValueTable(entries = []) {
  return {
    table: {
      widths: [170, '*'],
      body: entries.map(([label, value]) => ([
        { text: label, style: 'labelCell' },
        { text: value == null || value === '' ? '-' : String(value), style: 'valueCell' },
      ])),
    },
    layout: 'lightHorizontalLines',
    margin: [0, 0, 0, 10],
  };
}

function buildTable(header, rows, widths) {
  return {
    table: {
      headerRows: 1,
      widths,
      body: [
        header.map((item) => ({ text: item, style: 'tableHeader' })),
        ...rows,
      ],
    },
    layout: 'lightHorizontalLines',
    margin: [0, 4, 0, 10],
  };
}

function buildPracticalStations(practicalSheets = {}, candidateId) {
  return Object.values(practicalSheets || {}).map((sheet) => {
    const result = sheet?.rawPayload?.resultsByCandidateId?.[String(candidateId)] || null;
    return {
      stationCode: sheet?.stationCode || '',
      stationLabel: PRACTICAL_STATION_LABELS[sheet?.stationCode] || sheet?.stationCode || '-',
      sheetStatus: formatPracticalStatusLabel(sheet?.status),
      resultStatus: formatPracticalStatusLabel(result?.status),
      laneId: result?.laneId || '-',
      points: result?.points ?? '-',
      totalMs: Number.isFinite(Number(result?.totalMs)) && Number(result?.totalMs) > 0
        ? `${formatNumber(Number(result.totalMs) / 1000, 1)} s`
        : '-',
      remarks: result?.remarks || '-',
    };
  });
}

function buildWeeklySummaryRows(weeklySummaries = []) {
  return (weeklySummaries || []).map((summary) => ([
    { text: `Woche ${summary.weekNumber || '-'}`, style: 'valueCell' },
    { text: formatNumber(summary.averageScore), style: 'valueCell' },
    { text: summary.performanceLabel || '-', style: 'valueCell' },
    { text: `${formatNumber(summary.absenceHours)} h`, style: 'valueCell' },
    { text: summary.storedEvaluation?.decision_code || '-', style: 'valueCell' },
    { text: summary.storedEvaluation?.feedback_text || '-', style: 'valueCell' },
  ]));
}

function buildAssessmentStationRows(stations = []) {
  return (stations || []).map((item) => ([
    { text: item?.definition?.shortTitle || item?.definition?.title || item?.stationCode || '-', style: 'valueCell' },
    { text: `${Number(item?.station?.points || 0)} / ${Number(item?.station?.max_points || item?.definition?.maxPoints || 0)}`, style: 'valueCell' },
    { text: formatQualifiedLabel(item?.station?.passed), style: 'valueCell' },
    { text: formatPracticalStatusLabel(item?.station?.status), style: 'valueCell' },
    { text: item?.station?.remarks || '-', style: 'valueCell' },
  ]));
}

function buildParticipantCourseSection(participant) {
  const course = participant.course;
  if (!course) {
    return [
      { text: 'Lehrgang', style: 'sectionHeader' },
      { text: 'Kein zugeordneter Lehrgang gefunden.', style: 'mutedText', margin: [0, 0, 0, 10] },
    ];
  }

  const content = [
    { text: 'Lehrgang', style: 'sectionHeader' },
    buildKeyValueTable([
      ['Lehrgang', course.courseLabel || '-'],
      ['Zeitraum', `${formatDate(course.startDate)} bis ${formatDate(course.endDate)}`],
      ['Status', course.statusLabel || formatExamStatusLabel(course.status)],
      ['Fehlzeiten', `${formatNumber(course.totalAbsenceHours)} h`],
      ['Prüfungszulassung', course.examAdmissionStatusLabel || '-'],
      ['Gesamtstunden', `${formatNumber(course.totalHours)} h`],
    ]),
  ];

  if (Array.isArray(course.weeklySummaries) && course.weeklySummaries.length > 0) {
    content.push({ text: 'Wochenauswertungen', style: 'subHeader' });
    content.push(buildTable(
      ['Woche', 'Durchschnitt', 'Leistung', 'Fehlzeiten', 'Entscheidung', 'Feedback'],
      buildWeeklySummaryRows(course.weeklySummaries),
      [50, 60, 85, 65, 95, '*'],
    ));
  }

  if (course.exclusionRecord) {
    content.push({ text: 'Ausschlussverfahren', style: 'subHeader' });
    content.push(buildKeyValueTable([
      ['Status', formatPracticalStatusLabel(course.exclusionRecord.status)],
      ['Ausschluss bestätigt', course.exclusionRecord.exclusionConfirmed ? 'Ja' : 'Nein'],
      ['Mitteilung an Teilnehmer', formatDate(course.exclusionRecord.participantNotifiedDate)],
      ['Rückzug', course.exclusionRecord.withdrawnOnDayLabel || '-'],
    ]));
    content.push({ text: `Begründung: ${course.exclusionRecord.detailedReasonText || '-'}`, style: 'valueText', margin: [0, 0, 0, 10] });
  }

  return content;
}

function buildParticipantAssessmentSection(participant) {
  const events = participant.assessmentEvents || [];
  if (events.length === 0) {
    return [
      { text: 'Eignungsverfahren', style: 'sectionHeader' },
      { text: 'Kein zugeordnetes Eignungsverfahren gefunden.', style: 'mutedText', margin: [0, 0, 0, 10] },
    ];
  }

  const content = [{ text: 'Eignungsverfahren', style: 'sectionHeader' }];

  events.forEach((event) => {
    content.push({ text: event.label || 'Eignungsverfahren', style: 'subHeader' });
    content.push(buildKeyValueTable([
      ['Datum', formatDate(event.assessmentDate)],
      ['Status', formatExamStatusLabel(event.status)],
      ['Startnummer', event.candidate?.startNumber || '-'],
      ['Punkte', `${Number(event.candidate?.totalPoints || 0)} / ${Number(event.candidate?.maxPoints || 0)}`],
      ['Quote', `${formatNumber(event.candidate?.percentScore || 0)} %`],
      ['Ergebnis', formatQualifiedLabel(event.candidate?.qualified)],
    ]));
    content.push(buildTable(
      ['Station', 'Punkte', 'Bestanden', 'Status', 'Bemerkung'],
      buildAssessmentStationRows(event.stations),
      [90, 55, 70, 70, '*'],
    ));
  });

  return content;
}

function buildParticipantExamSection(participant) {
  const writtenResult = participant.writtenResult || null;
  const practicalStations = participant.practicalStations || [];

  const content = [
    { text: 'Prüfung SRHT', style: 'sectionHeader' },
    buildKeyValueTable([
      ['Rolle', participant.roleVariantLabel || '-'],
      ['Schriftliche Punkte', writtenResult ? Number(writtenResult.points || 0) : '-'],
      ['Note', writtenResult ? Number(writtenResult.grade || 0) : '-'],
      ['Status schriftlich', writtenResult ? formatPracticalStatusLabel(writtenResult.status) : 'Noch nicht erfasst'],
      ['Finalisiert am', writtenResult ? formatDateTime(writtenResult.finalizedAt) : '-'],
    ]),
  ];

  if (practicalStations.length > 0) {
    content.push({ text: 'Praktische Prüfbögen', style: 'subHeader' });
    content.push(buildTable(
      ['Prüfbogen', 'Bogenstatus', 'Ergebnisstatus', 'Bahn', 'Wert', 'Bemerkung'],
      practicalStations.map((station) => ([
        { text: station.stationLabel, style: 'valueCell' },
        { text: station.sheetStatus, style: 'valueCell' },
        { text: station.resultStatus, style: 'valueCell' },
        { text: station.laneId, style: 'valueCell' },
        { text: station.totalMs !== '-' ? station.totalMs : String(station.points), style: 'valueCell' },
        { text: station.remarks, style: 'valueCell' },
      ])),
      [120, 70, 75, 45, 55, '*'],
    ));
  }

  return content;
}

function buildSelectedCandidateIdSet(selectedCandidateIds = [], selectedCandidateId = null) {
  const values = Array.isArray(selectedCandidateIds)
    ? selectedCandidateIds
    : (selectedCandidateId ? [selectedCandidateId] : []);

  return new Set(values.map((value) => String(value)).filter(Boolean));
}

function buildAssessmentEventsForCandidate({ candidate, courseDetail, assessmentPackagesById }) {
  const assessmentEventIds = (courseDetail?.assessmentSources || []).map((item) => Number(item.assessment_event_id));

  return assessmentEventIds.map((assessmentEventId) => {
    const assessmentPackage = assessmentPackagesById[String(assessmentEventId)] || null;
    const assessmentCandidate = assessmentPackage?.candidates?.find((item) => String(item?.candidateMeta?.user_id) === String(candidate.userId)) || null;
    return {
      eventId: assessmentEventId,
      eventCandidateId: Number(
        assessmentCandidate?.candidateMeta?.event_candidate_id
        || assessmentCandidate?.candidate?.eventCandidateId
        || 0,
      ),
      label: assessmentPackage?.event?.label || null,
      assessmentDate: assessmentPackage?.event?.assessment_date || null,
      status: assessmentPackage?.event?.status || null,
      candidate: assessmentCandidate?.candidate || null,
      stations: assessmentCandidate?.stations || [],
    };
  }).filter((item) => item.label && item.candidate);
}

function resolveAssessmentEventsForCandidate({ candidate, courseDetail, assessmentPackagesById, selectedAssessmentEventIdsByUserId = {} }) {
  const configuredEventIds = Array.isArray(selectedAssessmentEventIdsByUserId?.[String(candidate.userId)])
    ? selectedAssessmentEventIdsByUserId[String(candidate.userId)].map((value) => Number(value)).filter((value) => Number.isInteger(value) && value > 0)
    : [];
  const preferredEventId = configuredEventIds.length === 1 ? configuredEventIds[0] : null;

  const resolvedEvents = buildAssessmentEventsForCandidate({
    candidate,
    courseDetail,
    assessmentPackagesById,
  });

  if (!resolvedEvents.length) {
    return [];
  }

  if (preferredEventId) {
    return resolvedEvents.filter((item) => Number(item.eventId) === preferredEventId);
  }

  const passedEvents = resolvedEvents.filter((item) => item.candidate?.qualified === true);
  if (passedEvents.length === 1) {
    return passedEvents;
  }

  return resolvedEvents;
}

export function getSrhtExamAssessmentSelectionCandidates({
  candidates = [],
  courseDetailsById = {},
  assessmentPackagesById = {},
  selectedAssessmentEventIdsByUserId = {},
}) {
  return (Array.isArray(candidates) ? candidates : []).map((candidate) => {
    const courseDetail = candidate?.courseId ? courseDetailsById[String(candidate.courseId)] || null : null;
    const assessmentEvents = buildAssessmentEventsForCandidate({
      candidate,
      courseDetail,
      assessmentPackagesById,
    });
    const configuredEventIds = Array.isArray(selectedAssessmentEventIdsByUserId?.[String(candidate.userId)])
      ? selectedAssessmentEventIdsByUserId[String(candidate.userId)].map((value) => Number(value)).filter((value) => Number.isInteger(value) && value > 0)
      : [];
    const passedEvents = assessmentEvents.filter((item) => item.candidate?.qualified === true);

    if (assessmentEvents.length <= 1 || configuredEventIds.length === 1 || passedEvents.length === 1) {
      return null;
    }

    return {
      candidateId: Number(candidate.id || 0),
      userId: Number(candidate.userId || 0),
      fullName: candidate.fullName || '-',
      events: assessmentEvents.map((event) => ({
        eventId: Number(event.eventId || 0),
        label: event.label || 'Eignungsverfahren',
        assessmentDate: event.assessmentDate || null,
        qualified: event.candidate?.qualified === null ? null : Boolean(event.candidate?.qualified),
      })),
    };
  }).filter(Boolean);
}

export function buildSrhtExamExportModel({
  examDetail,
  courseDetailsById = {},
  assessmentPackagesById = {},
  variant,
  selectedCandidateId = null,
  selectedCandidateIds = [],
  selectedAssessmentEventIdsByUserId = {},
}) {
  const option = SRHT_EXAM_PDF_OPTIONS.find((item) => item.value === variant) || SRHT_EXAM_PDF_OPTIONS[0];
  const candidates = Array.isArray(examDetail?.candidates) ? examDetail.candidates : [];
  const selectedCandidateIdSet = buildSelectedCandidateIdSet(selectedCandidateIds, selectedCandidateId);
  const filteredCandidates = option.requiresParticipantSelection
    ? candidates.filter((candidate) => selectedCandidateIdSet.has(String(candidate.id)))
    : candidates;
  const filteredExamDetail = {
    ...(examDetail || {}),
    candidates: filteredCandidates,
  };

  const participants = filteredCandidates.map((candidate) => {
    const courseDetail = candidate.courseId ? courseDetailsById[String(candidate.courseId)] || null : null;
    const courseParticipant = courseDetail?.participants?.find((item) => String(item.id) === String(candidate.courseParticipantId || ''))
      || courseDetail?.participants?.find((item) => String(item.user_id) === String(candidate.userId))
      || null;
    const assessmentEvents = resolveAssessmentEventsForCandidate({
      candidate,
      courseDetail,
      assessmentPackagesById,
      selectedAssessmentEventIdsByUserId,
    });

    return {
      candidateId: Number(candidate.id),
      courseParticipantId: Number(courseParticipant?.id || candidate.courseParticipantId || 0),
      fullName: candidate.fullName || '-',
      email: candidate.email || '',
      roleVariantLabel: formatCandidateRoleVariant(candidate.candidateRoleVariant),
      writtenResult: option.includesExam ? (candidate.writtenResult || null) : null,
      practicalStations: option.includesExam ? buildPracticalStations(examDetail?.practicalSheets || {}, candidate.id) : [],
      course: option.includesCourse && courseDetail ? {
        courseId: Number(courseDetail.course?.id || candidate.courseId || 0),
        courseParticipantId: Number(courseParticipant?.id || candidate.courseParticipantId || 0),
        courseLabel: courseDetail.course?.label || candidate.courseLabel || '-',
        startDate: courseDetail.course?.start_date || '',
        endDate: courseDetail.course?.end_date || '',
        status: courseDetail.course?.status || '',
        statusLabel: courseParticipant?.statusLabel || '-',
        totalHours: Number(courseDetail.course?.total_hours || 0),
        totalAbsenceHours: Number(courseParticipant?.total_absence_hours || 0),
        examAdmissionStatusLabel: courseParticipant?.examAdmissionStatusLabel || '-',
        weeklySummaries: courseParticipant?.weeklySummaries || [],
        exclusionRecord: courseParticipant?.exclusionRecord || null,
      } : null,
      assessmentEvents: option.includesAssessment ? assessmentEvents : [],
    };
  });

  return {
    exportType: option.value,
    exportLabel: option.label,
    exam: {
      id: Number(examDetail?.exam?.id || 0),
      label: examDetail?.exam?.label || 'Prüfung SRHT',
      writtenExamDate: examDetail?.exam?.written_exam_date || '',
      practicalExamDate: examDetail?.exam?.practical_exam_date || examDetail?.exam?.exam_date || '',
      location: examDetail?.exam?.location || '',
      status: examDetail?.exam?.status || 'draft',
      notes: examDetail?.exam?.notes || '',
      allResultsReleased: Boolean(examDetail?.exam?.allResultsReleased || examDetail?.exam?.all_results_released),
      sourceCourseCount: Array.isArray(examDetail?.sourceCourses) ? examDetail.sourceCourses.length : 0,
    },
    sourceCourses: examDetail?.sourceCourses || [],
    staff: examDetail?.staff || {},
    practicalSheets: examDetail?.practicalSheets || {},
    oralRetakes: examDetail?.oralRetakes || [],
    finalEvaluation: buildSrhtFinalEvaluation(filteredExamDetail),
    participants,
    generatedAt: new Date().toISOString(),
  };
}

export function buildSrhtExamPdfFilename(model = {}) {
  const examLabel = sanitizeFilenamePart(model?.exam?.label || 'Pruefung-SRHT') || 'Pruefung-SRHT';
  const singleParticipantName = model?.participants?.length === 1
    ? sanitizeFilenamePart(model.participants[0]?.fullName || '')
    : '';
  const exportType = String(model?.exportType || 'exam-report-all');
  const kind = exportType.startsWith('full-chain')
    ? 'Gesamtbericht Eignungsverfahren + Lehrgang + Prüfung'
    : 'Gesamtbericht Prüfung';
  const suffix = singleParticipantName ? ` - ${singleParticipantName}` : '';
  return `${kind} ${examLabel}${suffix}.pdf`;
}

export function buildSrhtExamPdfDocument({ model, brandingAssets = {} }) {
  const participantCount = Array.isArray(model?.participants) ? model.participants.length : 0;
  const useLegacyFullChainParticipantLayout = String(model?.exportType || '').startsWith('full-chain')
    && model?.legacyFullChainParticipantLayout === true;

  const content = useLegacyFullChainParticipantLayout
    ? [
      {
        columns: [
          brandingAssets.feuerwehrLogo ? { image: brandingAssets.feuerwehrLogo, width: 72 } : { text: '' },
          {
            width: '*',
            stack: [
              { text: 'Prüfung SRHT', style: 'title' },
              { text: model?.exportLabel || 'PDF-Export', style: 'subtitle' },
              { text: `Erstellt am ${formatDateTime(model?.generatedAt)} für ${participantCount} Teilnehmer`, style: 'meta' },
            ],
            alignment: 'center',
          },
          brandingAssets.hoehenrettungLogo ? { image: brandingAssets.hoehenrettungLogo, width: 54, alignment: 'right' } : { text: '' },
        ],
        margin: [0, 0, 0, 16],
      },
      { text: 'Übersicht', style: 'sectionHeader' },
      buildKeyValueTable([
        ['Prüfung', model?.exam?.label || '-'],
        ['Schriftliche Prüfung', formatDate(model?.exam?.writtenExamDate)],
        ['Praktische Prüfung', formatDate(model?.exam?.practicalExamDate)],
        ['Ort', model?.exam?.location || '-'],
        ['Status', formatExamStatusLabel(model?.exam?.status)],
        ['Quelllehrgänge', String(model?.exam?.sourceCourseCount || 0)],
        ['Teilnehmer', String(participantCount)],
      ]),
      ...(model?.exam?.notes ? [{ text: `Notizen: ${model.exam.notes}`, style: 'valueText', margin: [0, 0, 0, 12] }] : []),
      ...(model?.participants || []).flatMap((participant, index) => ([
        {
          text: participant.fullName || '-',
          style: 'participantHeader',
          margin: [0, index === 0 ? 2 : 14, 0, 6],
          pageBreak: index > 0 ? 'before' : undefined,
        },
        buildKeyValueTable([
          ['E-Mail', participant.email || '-'],
          ['Rolle', participant.roleVariantLabel || '-'],
        ]),
        ...buildParticipantAssessmentSection(participant),
        ...buildParticipantCourseSection(participant),
        ...buildParticipantExamSection(participant),
      ])),
    ]
    : buildSrhtExamOverallReportContent({ model, brandingAssets });

  return {
    pageSize: 'A4',
    pageMargins: [32, 32, 32, 32],
    content,
    styles: {
      title: {
        fontSize: 20,
        bold: true,
        color: '#1b3a5c',
      },
      subtitle: {
        fontSize: 11,
        color: '#3d4b57',
        margin: [0, 2, 0, 2],
      },
      meta: {
        fontSize: 9,
        color: '#5f6b76',
      },
      sectionHeader: {
        fontSize: 15,
        bold: true,
        color: '#1b3a5c',
        margin: [0, 8, 0, 6],
      },
      subHeader: {
        fontSize: 12,
        bold: true,
        color: '#1b3a5c',
        margin: [0, 4, 0, 4],
      },
      detailSectionHeader: {
        fontSize: 13,
        bold: true,
        color: '#1b3a5c',
        margin: [0, 8, 0, 4],
      },
      detailSubHeader: {
        fontSize: 11,
        bold: true,
        color: '#1f2d3a',
        margin: [0, 6, 0, 4],
      },
      participantHeader: {
        fontSize: 16,
        bold: true,
        color: '#0f1720',
      },
      tableHeader: {
        bold: true,
        fillColor: '#e8eef4',
        color: '#1b3a5c',
        margin: [0, 3, 0, 3],
      },
      labelCell: {
        bold: true,
        color: '#1f2d3a',
        margin: [0, 3, 0, 3],
      },
      valueCell: {
        margin: [0, 3, 0, 3],
      },
      valueText: {
        fontSize: 10,
      },
      mutedText: {
        fontSize: 10,
        color: '#5f6b76',
      },
    },
    defaultStyle: {
      fontSize: 10,
      color: '#111827',
    },
  };
}